import React, { useEffect, useState } from "react";
import { Button, TextField, CircularProgress, FormControlLabel, Switch, Checkbox, Box, Typography } from "@mui/material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import ShiftApiViewController from "../../redux/actions/shiftApiAction";
import FormDrawer from "../../components/layout/FormDrawer";
import { primaryButtonSx, inputSx } from "../../components/auth/authTheme";
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import dayjs from "dayjs";

import "../../styles/styles.css";
import "../../styles/layout.css";

const validationSchema = Yup.object({
  shift_name: Yup.string().trim().required("Shift name is required"),
  start_time: Yup.string().required("Start time is required"),
  end_time: Yup.string().required("End time is required"),
});

function AddShiftForm({ open, handleClose, editData = null }) {
  const dispatch = useDispatch();
  const { addShift, updateShift } = ShiftApiViewController();
  const { loading, shiftsData } = useSelector((state) => state.shifts);

  const isEdit = Boolean(editData?.id);

  const [initialValues, setInitialValues] = useState({ 
    shift_name: "",
    start_time: "",
    end_time: "",
    is_default: false,
    is_active: true
  });

  useEffect(() => {
    if (!open) return;
    setInitialValues({
      shift_name: editData?.shift_name || "",
      start_time: editData?.start_time || "",
      end_time: editData?.end_time || "",
      is_default: editData?.is_default || false,
      is_active: editData?.is_active ?? true,
    });
  }, [open, editData]);

  const handleSubmit = async (values, { resetForm, setSubmitting }) => {
    try {
      const action = isEdit
        ? updateShift(editData.id, values)
        : addShift(values);
      await dispatch(action);
      resetForm();
      handleClose();
    } catch (error) {
      console.error("Error submitting shift form:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const getDayjsTime = (timeString) => {
    if (!timeString) return null;
    const [hours, minutes] = timeString.split(":");
    return dayjs().set('hour', hours).set('minute', minutes);
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      enableReinitialize
      validateOnBlur
      validateOnChange={false}
      onSubmit={handleSubmit}
    >
      {({
        values,
        errors,
        touched,
        isSubmitting,
        submitForm,
        handleChange,
        handleBlur,
        setFieldValue
      }) => {
        const isSaving = isSubmitting || loading;

        // Check if there's already a default shift in the company
        const existingDefaultShift = (shiftsData || []).find(s => s.is_default);
        const hasOtherDefault = existingDefaultShift && (!isEdit || existingDefaultShift.id !== editData?.id);

        return (
          <Form style={{ display: "contents" }}>
            <FormDrawer
              open={open}
              onClose={handleClose}
              title={isEdit ? "Edit Shift" : "Add Shift"}
              subtitle={
                isEdit
                  ? "Update shift details"
                  : "Create a new shift for your organization"
              }
              footer={
                <>
                  <Button onClick={handleClose} color="inherit" disabled={isSaving}>
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    variant="contained"
                    color="primary"
                    disabled={isSaving}
                    onClick={() => submitForm()}
                    sx={{
                      ...primaryButtonSx,
                      minWidth: 120,
                      "&.Mui-disabled": {
                        bgcolor: isSaving ? "#FF6A00" : undefined,
                        color: isSaving ? "#fff" : undefined,
                        opacity: isSaving ? 0.85 : undefined,
                      },
                    }}
                    startIcon={
                      isSaving ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : null
                    }
                  >
                    {isSaving
                      ? isEdit
                        ? "Updating..."
                        : "Creating..."
                      : isEdit
                        ? "Update"
                        : "Create"}
                  </Button>
                </>
              }
            >
              <TextField
                label="Shift Name"
                name="shift_name"
                value={values.shift_name}
                onChange={handleChange}
                onBlur={handleBlur}
                fullWidth
                required
                placeholder="e.g. Morning Shift"
                error={touched.shift_name && Boolean(errors.shift_name)}
                helperText={touched.shift_name && errors.shift_name}
                sx={{ ...inputSx, mb: 3 }}
              />

              <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <TimePicker
                  label="Start Time"
                  value={getDayjsTime(values.start_time)}
                  onChange={(newValue) => {
                    setFieldValue("start_time", newValue ? newValue.format('HH:mm:ss') : "");
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      error: touched.start_time && Boolean(errors.start_time),
                      helperText: touched.start_time && errors.start_time,
                      sx: { ...inputSx }
                    },
                  }}
                />

                <TimePicker
                  label="End Time"
                  value={getDayjsTime(values.end_time)}
                  onChange={(newValue) => {
                    setFieldValue("end_time", newValue ? newValue.format('HH:mm:ss') : "");
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      error: touched.end_time && Boolean(errors.end_time),
                      helperText: touched.end_time && errors.end_time,
                      sx: { ...inputSx }
                    },
                  }}
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={values.is_default}
                      onChange={(e) => setFieldValue("is_default", e.target.checked)}
                      color="primary"
                      disabled={hasOtherDefault}
                    />
                  }
                  label="Set as Default Shift"
                />
                <Typography variant="caption" color={hasOtherDefault ? "error" : "textSecondary"} sx={{ display: 'block', ml: 4, mt: -1 }}>
                  {hasOtherDefault 
                    ? "A default shift already exists. To set this as default, you must first remove the default status from the existing default shift."
                    : "Note: Only one shift can be set as the default shift for the company."}
                </Typography>
              </Box>

              <FormControlLabel
                control={
                  <Switch
                    checked={values.is_active}
                    onChange={(e) => setFieldValue("is_active", e.target.checked)}
                    color="primary"
                  />
                }
                label="Is Active"
                sx={{ display: 'block' }}
              />
            </FormDrawer>
          </Form>
        );
      }}
    </Formik>
  );
}

export default AddShiftForm;
